import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { cloneDeep, isEqual } from 'lodash-es';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Stack,
  Typography,
  Box,
  Checkbox,
  Switch,
  TextField,
  MenuItem,
} from '@mui/material';
import { useDrag, useDrop } from 'react-dnd';

import { IPortal, Schema, useStore } from '@mcn-platform/models';
import { ConversationWidget } from '@mcn-platform/portal-lib';
import {
  Search,
  ConversationStoreProvider,
  getShoppingAssistantSystemPromptOverride,
} from '@mcn-platform/converse-lib';
import ConversationStoreAdapter from '@mcn-platform/portal-lib/src/portal/clientAi/ConversationStoreAdapter';
import usePrompt from 'hooks/usePrompt';
import ItemEdit from 'components/item/ItemEdit';
import { FieldType, FieldWithPromptKeyType } from 'components/forms/FieldTypes';
import AnswerLengthControl from 'publisher/editor/controls/ai/AnswerLengthControl';
import ColorControl from 'publisher/editor/controls/ColorControl';
import HelperText from 'components/forms/HelperText';
import StandardNumberField from 'components/forms/StandardNumberField';
import StandardColorField from 'components/forms/StandardColorField';
import StandardSelectField from 'components/forms/StandardSelectField';
import StandardTextField from 'components/forms/StandardTextField';
import InstructionFieldWithPromptModel from 'components/clientAI/InstructionFieldWithPromptModal';
import PromptTestingField from 'components/clientAI/PromptTestingField';
import PortalSuggestedPromptsPanel from 'components/clientAI/PortalSuggestedPromptsPanel';
import ProductStructuredDataConfigControl from 'components/clientAI/ProductStructuredDataConfigControl';
import SelectionWizardToolField from 'components/clientAI/SelectionWizardToolField';
import PgVectorSearchPanel from 'components/clientAI/PgVectorSearchPanel';
import StandardBulletPointsField from 'components/forms/StandardBulletPointsField';
import StandardCheckboxField from 'components/forms/StandardCheckboxField';
import AssetReferenceField from 'components/forms/AssetReferenceField';
import LocalizedTextArrayControl from 'publisher/editor/controls/LocalizedTextArrayControl';
import LocalizedTextControl from 'publisher/editor/controls/LocalizedTextControl';
import HamburgerMenuLinksControl from 'publisher/editor/controls/HamburgerMenuLinksControl';
import EditLayout from './EditLayout';
import { BasePromptModalProvider } from '../contexts/BasePromptModalContext';
import { SHOPPING_ASSISTANT_PROMPT, AI_MODE_SEARCH_PROMPT, DUAL_SEARCH_SYSTEM_PROMPT } from '../constants/systemPromptDefaults';
import { prepareDirtyPortalAIConfig } from '../components/clientAI/portalAIConfigPersistence';
import { CLIENT_AI_MODEL_OPTIONS } from '../constants/clientAIModelOptions';

const DND_TYPE_PRODUCT_CARD = 'productCardElementOrder';
const GUIDE_PRODUCT_POPUP_DEFAULT_CONFIG = {
  enabled: false,
  size: {
    widthPx: null,
    heightPx: null,
  },
  autoOpen: {
    delayMs: 0,
  },
  content: {
    heading: 'Featured products from this guide',
    addToCartLabel: 'Add to cart',
    addingLabel: 'Adding...',
    addedLabel: 'Added',
    unavailableLabel: 'Unavailable',
    retryLabel: 'Try again',
    errorLabel: 'Could not add this item. Please try again.',
    variantLabel: 'Options',
  },
  products: {
    source: 'guide_linked_products',
    maxItems: 12,
  },
};
const moveIndex = (list: string[], from: number, to: number) => {
  const copy = list.slice();
  const [moved] = copy.splice(from, 1);
  copy.splice(to, 0, moved);
  return copy;
};

const ReorderRow = ({
  meta,
  index,
  checked,
  onToggle,
  onMove,
}: any) => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [, drop] = useDrop({
    accept: DND_TYPE_PRODUCT_CARD,
    hover: (item: any, monitor) => {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      const hoverRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      onMove(dragIndex, hoverIndex);
      // eslint-disable-next-line no-param-reassign
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: DND_TYPE_PRODUCT_CARD,
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });
  drag(drop(ref));
  return (
    <Stack ref={ref} direction="row" alignItems="center" spacing={2} sx={{ mb: 1, opacity: isDragging ? 0.6 : 1 }}>
      <Box
        sx={{
          flex: 1,
          border: '1px solid',
          borderColor: '#0f2b3a',
          borderRadius: 1,
          px: 2,
          py: 1,
          cursor: 'grab',
        }}
      >
        <Typography>{meta.label}</Typography>
      </Box>
      <Checkbox
        checked={checked}
        onChange={(e) => onToggle(e.target.checked)}
        inputProps={{ 'aria-label': `${meta.label} enabled` }}
      />
    </Stack>
  );
};

// Product Card element order editor (DnD + checkboxes)
const PRODUCT_CARD_ELEMENTS: Array<{ key: string; label: string; flag: string; defaultOn?: boolean }> = [
  { key: 'productName', label: 'Title', flag: 'showTitle', defaultOn: true },
  { key: 'description', label: 'Description', flag: 'showDescription', defaultOn: true },
  // { key: 'ratings', label: 'Ratings', flag: 'showRatings' }, // TODO: need rating info API
  { key: 'pricing', label: 'Price', flag: 'showPrice', defaultOn: true },
  { key: 'variantSelection', label: 'Variant Selector', flag: 'showVariantSelection', defaultOn: true },
  { key: 'addToCartButton', label: 'Add to Cart Button', flag: 'showAddToCartButton', defaultOn: true },
  { key: 'distance', label: 'Distance', flag: 'showDistance', defaultOn: false },
];

const ProductCardOrderEditor = ({
  value,
  onChange,
}: {
  value: any;
  onChange: (v: any) => void;
}) => {
  const rawOrder: string[] = Array.isArray(value?.elementOrder)
    ? value.elementOrder
    : PRODUCT_CARD_ELEMENTS.map((i) => i.key);
  // Append any known elements missing from the saved order so new elements
  // (e.g. distance) show up in the editor without forcing a migration.
  const order: string[] = [...rawOrder];
  for (const el of PRODUCT_CARD_ELEMENTS) {
    if (!order.includes(el.key)) order.push(el.key);
  }

  const handleMove = (from: number, to: number) => onChange({
    ...(value || {}),
    elementOrder: moveIndex(order, from, to),
  });

  return (
    <Box>
      {order.map((k, index) => {
        const meta = PRODUCT_CARD_ELEMENTS.find((i) => i.key === k);
        if (!meta) return null;
        const defaultOn = meta.defaultOn !== false;
        const checked = Boolean(value?.[meta.flag] ?? defaultOn);
        return (
          <ReorderRow
            key={k}
            meta={meta}
            index={index}
            checked={checked}
            onToggle={(next: boolean) => onChange({ ...(value || {}), [meta.flag]: next })}
            onMove={handleMove}
          />
        );
      })}
    </Box>
  );
};

type ProductCardGatingProps = {
  control: Control;
  fieldName: string;
  readOnly: boolean;
  value: any;
  onChange: (v: any) => void;
};

const EnhancedSection: React.FC<Omit<ProductCardGatingProps, 'control' | 'fieldName'>> = ({
  readOnly,
  value,
  onChange,
}) => {
  const { currentPortal: portal } = useStore();
  const enhancedEnabled = Boolean(
    portal?.clientAIConfig?.enhancedProductCardEnabled,
  );
  return (
    <Box sx={{ mb: 3, width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Enhanced Product Card
      </Typography>
      <Box
        sx={{
          mb: 3,
          width: '100%',
          opacity: enhancedEnabled ? 1 : 0.5,
          pointerEvents: enhancedEnabled ? 'auto' : 'none',
        }}
      >
        <TextField
          select
          label="Orientation"
          value={value?.orientation ?? 'vertical'}
          onChange={(e) => {
            onChange({
              ...(value || {}),
              orientation: e.target.value as 'horizontal' | 'vertical',
            });
          }}
          disabled={readOnly}
          fullWidth
        >
          <MenuItem value="horizontal">Horizontal</MenuItem>
          <MenuItem value="vertical">Vertical</MenuItem>
        </TextField>
      </Box>

      <Box
        sx={{
          mb: 3,
          width: '100%',
          opacity: enhancedEnabled ? 1 : 0.5,
          pointerEvents: enhancedEnabled ? 'auto' : 'none',
        }}
      >
        <TextField
          select
          label="Image Fit"
          value={value?.objectFit ?? 'cover'}
          onChange={(e) => {
            onChange({
              ...(value || {}),
              objectFit: e.target.value as 'contain' | 'cover' | 'cover-full-height',
            });
          }}
          disabled={readOnly}
          fullWidth
        >
          <MenuItem value="contain">Contain (show full image)</MenuItem>
          <MenuItem value="cover">Cover (crop to fit)</MenuItem>
          <MenuItem value="cover-full-height">Cover - Full Height</MenuItem>
        </TextField>
      </Box>

      <Box
        sx={{
          mb: 3,
          width: '100%',
          opacity: enhancedEnabled ? 1 : 0.5,
          pointerEvents: enhancedEnabled ? 'auto' : 'none',
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Width Configuration
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Horizontal Width"
            type="number"
            value={value?.horizontalWidth ?? 450}
            onChange={(e) => onChange({
              ...(value || {}),
              horizontalWidth: Number(e.target.value) || 450,
            })}
            disabled={readOnly}
            fullWidth
            helperText="Width for horizontal orientation (px). Breakpoint: width + 25px"
            inputProps={{ min: 100, max: 1000 }}
          />
          <TextField
            label="Vertical Width"
            type="number"
            value={value?.verticalWidth ?? 150}
            onChange={(e) => onChange({
              ...(value || {}),
              verticalWidth: Number(e.target.value) || 150,
            })}
            disabled={readOnly}
            fullWidth
            helperText="Width for vertical orientation (px). Breakpoint: width + 25px"
            inputProps={{ min: 50, max: 500 }}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          mb: 3,
          width: '100%',
          opacity: enhancedEnabled ? 1 : 0.5,
          pointerEvents: enhancedEnabled ? 'auto' : 'none',
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Enhanced Product Format
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Items (Drag and Drop)
        </Typography>
        <ProductCardOrderEditor value={value} onChange={onChange} />
        <FormControlLabel
          sx={{ mt: 2, display: 'flex', alignItems: 'center' }}
          control={(
            <Switch
              checked={value?.showQuantitySelector !== false}
              onChange={(e) => onChange({
                ...(value || {}),
                showQuantitySelector: e.target.checked,
              })}
              disabled={readOnly}
            />
          )}
          label="Show Quantity Selector"
        />
        <HelperText
          name="showQuantitySelector"
          description="When off, the quantity control is hidden on the product card; add to cart still adds one item. Default: on (existing portals without this field behave as on)."
        />
      </Box>

      <Box
        sx={{
          mb: 3,
          width: '100%',
          opacity: enhancedEnabled ? 1 : 0.5,
          pointerEvents: enhancedEnabled ? 'auto' : 'none',
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Border
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <ColorControl
            label="Border Color"
            value={value?.borderStyle?.borderColor}
            onChange={(hex: string) => onChange({
              ...(value || {}),
              borderStyle: { ...(value?.borderStyle || {}), borderColor: hex },
            })}
            disabled={readOnly}
            showGlobalColors
          />
          <TextField
            label="Border Width"
            type="number"
            value={value?.borderStyle?.borderWidth ?? ''}
            onChange={(e) => onChange({
                ...(value || {}),
                borderStyle: {
                  ...(value?.borderStyle || {}),
                  borderWidth: Number(e.target.value || 0),
                },
              })}
            disabled={readOnly}
            fullWidth
          />
          <TextField
            label="Border Radius"
            type="number"
            value={value?.borderStyle?.borderRadius ?? ''}
            onChange={(e) => onChange({
              ...(value || {}),
              borderStyle: {
                ...(value?.borderStyle || {}),
                borderRadius: Number(e.target.value || 0),
              },
            })}
            disabled={readOnly}
            fullWidth
          />
        </Stack>
      </Box>

      <Box
        sx={{
          mb: 3,
          width: '100%',
          opacity: enhancedEnabled ? 1 : 0.5,
          pointerEvents: enhancedEnabled ? 'auto' : 'none',
        }}
      >
        <FormControlLabel
          control={(
            <Checkbox
              checked={value?.preferShopifyTitle ?? false}
              onChange={(e) => onChange({
                ...(value || {}),
                preferShopifyTitle: e.target.checked,
              })}
              disabled={readOnly}
            />
          )}
          label="Prefer Shopify Title (Localized)"
        />
        <HelperText
          name="preferShopifyTitle"
          description="When enabled, uses the localized title from Shopify instead of the search result title. Default: off (maintains current behavior for compatibility)."
        />
      </Box>

      <Box
        sx={{
          mb: 3,
          width: '100%',
          opacity: enhancedEnabled ? 1 : 0.5,
          pointerEvents: enhancedEnabled ? 'auto' : 'none',
        }}
      >
        <TextField
          label="Description max lines"
          type="number"
          value={value?.descriptionMaxLines ?? 3}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange({
              ...(value || {}),
              descriptionMaxLines: (v >= 1 && v <= 10) ? v : 3,
            });
          }}
          disabled={readOnly}
          fullWidth
          helperText="Max lines before 'See more' (1–10). Default: 3."
          inputProps={{ min: 1, max: 10 }}
          sx={{ mb: 2 }}
        />
      </Box>

      <Box
        sx={{
          mb: 3,
          width: '100%',
          opacity: enhancedEnabled ? 1 : 0.5,
          pointerEvents: enhancedEnabled ? 'auto' : 'none',
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Description Text Overrides
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="See More Text"
            value={value?.seeMoreText ?? ''}
            onChange={(e) => onChange({
              ...(value || {}),
              seeMoreText: e.target.value || undefined,
            })}
            disabled={readOnly}
            fullWidth
            helperText="Override for 'see more' button. Leave empty to use portal tokens."
          />
          <TextField
            label="See Less Text"
            value={value?.seeLessText ?? ''}
            onChange={(e) => onChange({
              ...(value || {}),
              seeLessText: e.target.value || undefined,
            })}
            disabled={readOnly}
            fullWidth
            helperText="Override for 'see less' button. Leave empty to use portal tokens."
          />
        </Stack>
      </Box>

      <Box
        sx={{
          mb: 3,
          width: '100%',
          opacity: enhancedEnabled ? 1 : 0.5,
          pointerEvents: enhancedEnabled ? 'auto' : 'none',
        }}
      />
    </Box>
  );
};

const AddToCartSection: React.FC<Omit<ProductCardGatingProps, 'value' | 'onChange'>> = ({
  control,
  fieldName,
  readOnly,
}) => {
  const { currentPortal: portal } = useStore();
  const addToCartEnabled = Boolean(
    portal?.clientAIConfig?.addToCartEnabled,
  );
  return (
    <Box
      sx={{
        mb: 3,
        width: '100%',
        opacity: addToCartEnabled ? 1 : 0.5,
        pointerEvents: addToCartEnabled ? 'auto' : 'none',
      }}
    >
      <Box sx={{ mb: 2, width: '100%' }}>
        <Stack spacing={2}>
          <StandardTextField
            control={control}
            fieldName={`${fieldName}.variantMessage`}
            field={{
              type: 'string',
              label: 'Variant Message',
              defaultTags: '',
              defaultPublicAccess: false,
            }}
            readOnly={readOnly}
          />
          <StandardTextField
            control={control}
            fieldName={`${fieldName}.buttonText`}
            field={{
              type: 'string',
              label: 'Add to Cart Button Text',
              defaultTags: '',
              defaultPublicAccess: false,
            }}
            readOnly={readOnly}
          />
          <StandardTextField
            control={control}
            fieldName={`${fieldName}.addedToCartText`}
            field={{
              type: 'string',
              label: 'Added to Cart Button Text',
              defaultTags: '',
              defaultPublicAccess: false,
              default: 'Added to Cart',
            }}
            readOnly={readOnly}
          />
          <StandardTextField
            control={control}
            fieldName={`${fieldName}.soldOutText`}
            field={{
              type: 'string',
              label: 'Sold Out Button Text',
              defaultTags: '',
              defaultPublicAccess: false,
              default: 'Sold Out',
            }}
            readOnly={readOnly}
          />
          <StandardTextField
            control={control}
            fieldName={`${fieldName}.loadingText`}
            field={{
              type: 'string',
              label: 'Loading Button Text',
              defaultTags: '',
              defaultPublicAccess: false,
              default: 'Processing...',
            }}
            readOnly={readOnly}
          />
          <StandardTextField
            control={control}
            fieldName={`${fieldName}.selectAllOptionsText`}
            field={{
              type: 'string',
              label: 'Select All Options Text',
              defaultTags: '',
              defaultPublicAccess: false,
              default: 'Please select all options',
            }}
            readOnly={readOnly}
          />
          <StandardTextField
            control={control}
            fieldName={`${fieldName}.seeMoreDetailsText`}
            field={{
              type: 'string',
              label: 'See More Details Button Text',
              defaultTags: '',
              defaultPublicAccess: false,
              default: 'See More Details',
            }}
            readOnly={readOnly}
          />
          <StandardTextField
            control={control}
            fieldName={`${fieldName}.checkoutText`}
            field={{
              type: 'string',
              label: 'Checkout Button Text',
              defaultTags: '',
              defaultPublicAccess: false,
              default: 'Check Out',
            }}
            readOnly={readOnly}
          />
          <Controller
            name={`${fieldName}.showCheckoutFlow`}
            control={control}
            defaultValue={true}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                label="Show Checkout Flow after Add to Cart"
                disabled={readOnly}
                control={(
                  <Checkbox
                    checked={value !== false}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                )}
              />
            )}
          />
        </Stack>
      </Box>
    </Box>
  );
};

const AddToCartButtonStyleSection: React.FC<{
  control: Control;
  fieldName: string;
  readOnly: boolean;
}> = ({ control, fieldName, readOnly }) => {
  const { currentPortal: portal } = useStore();
  const addToCartEnabled = Boolean(
    portal?.clientAIConfig?.addToCartEnabled,
  );
  return (
    <Box
      sx={{
        mb: 3,
        width: '100%',
        opacity: addToCartEnabled ? 1 : 0.5,
        pointerEvents: addToCartEnabled ? 'auto' : 'none',
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Add to Cart Button Style
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <StandardColorField
          control={control}
          fieldName={`${fieldName}.buttonStyle.backgroundColor`}
          field={{
            type: 'color',
            label: 'Background',
            defaultTags: '',
            defaultPublicAccess: false,
          }}
          readOnly={readOnly}
        />
        <StandardColorField
          control={control}
          fieldName={`${fieldName}.buttonStyle.color`}
          field={{
            type: 'color',
            label: 'Text',
            defaultTags: '',
            defaultPublicAccess: false,
          }}
          readOnly={readOnly}
        />
        <StandardNumberField
          control={control}
          fieldName={`${fieldName}.buttonStyle.borderRadius`}
          field={{
            type: 'number',
            label: 'Border Radius',
            defaultTags: '',
            defaultPublicAccess: false,
          }}
          readOnly={readOnly}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <StandardColorField
          control={control}
          fieldName={`${fieldName}.buttonStyle.hoverBackgroundColor`}
          field={{
            type: 'color',
            label: 'Hover Background',
            defaultTags: '',
            defaultPublicAccess: false,
          }}
          readOnly={readOnly}
        />
        <StandardColorField
          control={control}
          fieldName={`${fieldName}.buttonStyle.hoverColor`}
          field={{
            type: 'color',
            label: 'Hover Text',
            defaultTags: '',
            defaultPublicAccess: false,
          }}
          readOnly={readOnly}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <StandardColorField
          control={control}
          fieldName={`${fieldName}.buttonStyle.disabledBackgroundColor`}
          field={{
            type: 'color',
            label: 'Disabled Background',
            defaultTags: '',
            defaultPublicAccess: false,
          }}
          readOnly={readOnly}
        />
        <StandardColorField
          control={control}
          fieldName={`${fieldName}.buttonStyle.disabledColor`}
          field={{
            type: 'color',
            label: 'Disabled Text',
            defaultTags: '',
            defaultPublicAccess: false,
          }}
          readOnly={readOnly}
        />
      </Stack>
    </Box>
  );
};

const PostPurchaseSection: React.FC<Omit<ProductCardGatingProps, 'control'>> = ({
  readOnly,
  fieldName,
  value,
  onChange,
}) => {
  const { currentPortal: portal } = useStore();
  const addToCartEnabled = Boolean(
    portal?.clientAIConfig?.addToCartEnabled,
  );
  return (
    <Box
      sx={{
        mb: 3,
        width: '100%',
        opacity: addToCartEnabled ? 1 : 0.5,
        pointerEvents: addToCartEnabled ? 'auto' : 'none',
      }}
    >
      <FormControlLabel
        label="Enable Post-Purchase Flow"
        disabled={readOnly}
        control={(
          <Checkbox
            checked={value?.postPurchaseEnabled || false}
            onChange={(e) => onChange({
              ...value,
              postPurchaseEnabled: e.target.checked,
            })}
            name={`${fieldName}.postPurchaseEnabled`}
          />
        )}
      />
    </Box>
  );
};

const AnswerLengthControlType = (
  control: Control,
  fieldName: string,
  field:FieldType,
  readOnly: boolean,
) => (
  <Controller
    name={fieldName}
    control={control}
    render={({
      field: {
        onChange,
        isTouched,
        error,
        // onBlur,
        value,
      },
    }:any) => (
      <FormControl
        sx={{ width: '100%' }}
      >
        <FormControlLabel
          label={field.label || 'No Label'}
          disabled={field.readOnly || readOnly}
          labelPlacement="top"
          control={(
            <AnswerLengthControl
              label={field.label}
              value={value}
              onChange={onChange}
              disabled={readOnly}
            />
          )}
        />
        <HelperText
          name={fieldName}
          error={isTouched && error?.message}
          description={field.description}
        />
      </FormControl>
    )}
  />
);

// const OpenHoursControlType = (
//   control: Control,
//   fieldName: string,
//   field:FieldType,
//   readOnly: boolean,
// ) => (
//   <Controller
//     name={fieldName}
//     control={control}
//     render={({
//       field: {
//         onChange,
//         isTouched,
//         error,
//         // onBlur,
//         value,
//       },
//     }:any) => (
//       <FormControl
//         sx={{ width: '100%' }}
//       >
//         <FormControlLabel
//           label={field.label || 'No Label'}
//           disabled={field.readOnly || readOnly}
//           labelPlacement="top"
//           control={(
//             <Box sx={{ width: '100%' }}>
//               <Hours
//                 value={value}
//                 onChange={(osmString) => {
//                   console.log('onChange openSupportToolOpenHours', osmString);
//                   onChange(osmString);
//                 }}

//               />
//             </Box>
//           )}
//         />
//         <HelperText
//           name={fieldName}
//           error={isTouched && error?.message}
//           description={field.description}
//         />
//       </FormControl>
//     )}
//   />
// );

/** Same resolution as Prompt Testing / Bedrock buildSystemPrompt (via getShoppingAssistantSystemPromptOverride). */
const InvokeShoppingAssistantField = ({ portal }: { portal: IPortal }) => {
  const [modelOverride, setModelOverride] = useState('');
  const systemPromptOverride = getShoppingAssistantSystemPromptOverride(portal, SHOPPING_ASSISTANT_PROMPT);

  return (
    <Stack spacing={2}>
      <FormControl fullWidth size="small">
        <Typography variant="caption" sx={{ mb: 0.5 }}>
          Model Override (for testing)
        </Typography>
        <TextField
          select
          value={modelOverride}
          onChange={(e) => setModelOverride(e.target.value)}
          size="small"
          SelectProps={{
            MenuProps: {
              PaperProps: {
                style: { maxHeight: 400 },
              },
            },
          }}
        >
          {CLIENT_AI_MODEL_OPTIONS.map((option) => (
            <MenuItem key={option.value || 'default'} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </FormControl>
      <ConversationWidget
        key={`shopping-assistant-${modelOverride}`}
        limitToPageWidth={false}
        showItemReferencesWithMessage
        showItemReferencesCarousel={false}
        courseCardShowImage
        courseCardShowTitle
        courseCardShowInstructor={false}
        courseCardShowDescLines={3}
        questionStyle={{
          backgroundColor: '#dcdcdc',
          borderColor: '#9b9b9b',
          borderRadius: 8,
          borderWidth: 1,
          color: 'color_font',
          margin: 0,
          padding: 12,
          shadow: 1,
        }}
        responseStyle={{
          backgroundColor: 'color_background',
          borderColor: '#9b9b9b',
          borderRadius: 8,
          borderWidth: 1,
          color: 'color_font',
          margin: 0,
          padding: 12,
          shadow: 1,
        }}
        style={{
          backgroundColor: 'rgba(0,0,0,0)',
          borderColor: 'color_primary',
          borderRadius: 2,
          borderWidth: 0,
          color: 'color_font',
          margin: 4,
          padding: 4,
          shadow: 0,
        }}
        adminOptions={{
          systemPromptOverride,
          returnSystemPrompt: true,
          ...(modelOverride && { modelOverride }),
        }}
        openWebPagesInNewTab
        hideRightRailOnMobile
        maxItemsInRightRail={8}
      />
    </Stack>
  );
};

const SEARCH_FIELD_STYLES = {
  questionStyle: {
    backgroundColor: '#dcdcdc',
    borderColor: '#9b9b9b',
    borderRadius: 8,
    borderWidth: 1,
    color: 'color_font',
    margin: 0,
    padding: 12,
    shadow: 1,
  },
  responseStyle: {
    backgroundColor: 'color_background',
    borderColor: '#9b9b9b',
    borderRadius: 8,
    borderWidth: 1,
    color: 'color_font',
    margin: 0,
    padding: 12,
    shadow: 1,
  },
} as const;

/** AI / Dual Search: portal setting then system fallback only (never clientAIPromptTemplate). */
const getSystemPromptForSearchVariant = (
  portal: IPortal,
  variant: 'ai' | 'dual',
): string => {
  if (variant === 'ai') return portal.clientAIConfig?.aiModeSearchPrompt || AI_MODE_SEARCH_PROMPT;
  return (
    portal.clientAIConfig?.dualSearchPrompt
    || portal.clientAIConfig?.dualSearchSystemPromptTemplate
    || DUAL_SEARCH_SYSTEM_PROMPT
  );
};

const InvokeSearchField = ({
  portal,
  variant,
}: {
  portal: IPortal;
  variant: 'ai' | 'dual';
}) => {
  const store = useStore();
  const [modelOverride, setModelOverride] = useState('');
  const systemPromptOverride = getSystemPromptForSearchVariant(portal, variant);
  const keyPrefix = variant === 'ai' ? 'ai-mode-search' : 'dual-search';

  return (
    <ConversationStoreProvider value={new ConversationStoreAdapter(store)}>
      <Stack spacing={2}>
        <FormControl fullWidth size="small">
          <Typography variant="caption" sx={{ mb: 0.5 }}>
            Model Override (for testing)
          </Typography>
          <TextField
            select
            value={modelOverride}
            onChange={(e) => setModelOverride(e.target.value)}
            size="small"
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: { maxHeight: 400 },
                },
              },
            }}
          >
            {CLIENT_AI_MODEL_OPTIONS.map((option) => (
              <MenuItem key={option.value || 'default'} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
        <Search
          key={`${keyPrefix}-${modelOverride}`}
          adminOptions={{
            systemPromptOverride,
            returnSystemPrompt: true,
            ...(modelOverride && { modelOverride }),
          }}
          layoutMode={variant}
          enableMultiRefinements={false}
          hideRightRailOnMobile
          maxItemsInRightRail={8}
          openWebPagesInNewTab
          showItemReferencesWithMessage
          courseCardShowImage
          courseCardShowTitle
          courseCardShowInstructor={false}
          courseCardShowDescLines={3}
          questionStyle={SEARCH_FIELD_STYLES.questionStyle}
          responseStyle={SEARCH_FIELD_STYLES.responseStyle}
        />
      </Stack>
    </ConversationStoreProvider>
  );
};

/**
 * Shared header styling: tag, className, fontSize, fontWeight, color, alignment, margin.
 * Used by Suggested Prompts and Smart Summary (single source of truth).
 */
const HeaderStylingFields = ({
  control,
  fieldName,
  readOnly,
}: {
  control: Control;
  fieldName: string;
  readOnly: boolean;
}) => (
  <Box sx={{ mb: 3, width: '100%' }}>
    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
      Header Styling
    </Typography>
    <Stack direction="row" spacing={2} sx={{ width: '100%', mb: 2 }}>
      <Box sx={{ flex: 1 }}>
        <StandardSelectField
          control={control}
          fieldName={`${fieldName}.header.tag`}
          field={{
            type: 'string',
            label: 'Header tag',
            enum: ['h2', 'h3', 'h4', 'h5', 'h6', 'p'],
            enumLabels: ['H2', 'H3', 'H4', 'H5', 'H6', 'P'],
            defaultTags: '',
            defaultPublicAccess: false,
          }}
          readOnly={readOnly}
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <StandardTextField
          control={control}
          fieldName={`${fieldName}.header.className`}
          field={{
            type: 'string',
            label: 'Header class (optional)',
            defaultTags: '',
            defaultPublicAccess: false,
          }}
          readOnly={readOnly}
        />
      </Box>
    </Stack>
    <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
      <Box sx={{ flex: 1 }}>
        <StandardNumberField
          control={control}
          fieldName={`${fieldName}.header.fontSize`}
          field={{
            type: 'number',
            label: 'Font Size',
            defaultTags: '',
            defaultPublicAccess: false,
          }}
          readOnly={readOnly}
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <StandardNumberField
          control={control}
          fieldName={`${fieldName}.header.fontWeight`}
          field={{
            type: 'number',
            label: 'Font Weight',
            defaultTags: '',
            defaultPublicAccess: false,
          }}
          readOnly={readOnly}
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <StandardColorField
          control={control}
          fieldName={`${fieldName}.header.color`}
          field={{
            type: 'color',
            label: 'Color',
            defaultTags: '',
            defaultPublicAccess: false,
          }}
          readOnly={readOnly}
        />
      </Box>
    </Stack>
    <Stack direction="row" spacing={2} sx={{ mt: 2, width: '100%' }}>
      <Box sx={{ flex: 1 }}>
        <StandardSelectField
          control={control}
          fieldName={`${fieldName}.header.alignment`}
          field={{
            type: 'string',
            label: 'Alignment',
            enum: ['left', 'center', 'right'],
            enumLabels: ['Left', 'Center', 'Right'],
            defaultTags: '',
            defaultPublicAccess: false,
          }}
          readOnly={readOnly}
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <StandardNumberField
          control={control}
          fieldName={`${fieldName}.header.margin`}
          field={{
            type: 'number',
            label: 'Margin',
            defaultTags: '',
            defaultPublicAccess: false,
          }}
          readOnly={readOnly}
        />
      </Box>
    </Stack>
  </Box>
);

/** Single shared header config for Suggested Prompts and Smart Summary. */
const PageGeneratedContentHeaderControl = (
  control: Control,
  fieldName: string,
  field: FieldType,
  readOnly: boolean,
) => (
  <Controller
    name={fieldName}
    control={control}
    render={({ field: { isTouched, error } }: any) => (
      <FormControl sx={{ width: '100%' }}>
        <FormControlLabel
          label=""
          disabled={field.readOnly || readOnly}
          sx={{ width: '100%' }}
          control={(
            <Box
              sx={{
                width: '100%',
                border: '1px solid',
                borderColor: '#e0e0eb',
                borderRadius: 1,
                padding: 2,
                '&:hover': { borderColor: '#111111' },
                '&:focus-within': { border: '2px solid #1276dd' },
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Page-generated content header
              </Typography>
              <HeaderStylingFields control={control} fieldName={fieldName} readOnly={readOnly} />
            </Box>
          )}
        />
        <HelperText
          name={fieldName}
          error={isTouched && error?.message}
          description={field.description}
        />
      </FormControl>
    )}
  />
);

const SuggestedPromptsStyleControl = (
  control: Control,
  fieldName: string,
  field: FieldType,
  readOnly: boolean,
) => (
  <Controller
    name={fieldName}
    control={control}
    render={({
      field: {
        isTouched,
        error,
      },
    }: any) => (
      <FormControl
        sx={{ width: '100%' }}
      >
        <FormControlLabel
          label=""
          disabled={field.readOnly || readOnly}
          sx={{ width: '100%' }}
          control={(
            <Box
              sx={{
                width: '100%',
                border: '1px solid',
                borderColor: '#e0e0eb',
                borderRadius: 1,
                padding: 2,
                '&:hover': {
                  borderColor: '#111111',
                },
                '&:focus-within': {
                  border: '2px solid #1276dd',
                },
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Suggested Prompts Styling
              </Typography>

              {/* Layout: Vertical | Horizontal (non-scroll blocks only) */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <StandardSelectField
                  control={control}
                  fieldName={`${fieldName}.layout`}
                  field={{
                    type: 'string',
                    label: 'Chip Layout',
                    enum: ['vertical', 'horizontal'],
                    enumLabels: ['Vertical', 'Horizontal'],
                    defaultTags: '',
                    defaultPublicAccess: false,
                  }}
                  readOnly={readOnly}
                />
              </Box>

              {/* Button Styling - Default State */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Button Styling
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mb: 2, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.button.fontSize`}
                      field={{
                        type: 'number',
                        label: 'Font Size',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardColorField
                      control={control}
                      fieldName={`${fieldName}.button.color`}
                      field={{
                        type: 'color',
                        label: 'Text Color',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mb: 2, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardColorField
                      control={control}
                      fieldName={`${fieldName}.button.backgroundColor`}
                      field={{
                        type: 'color',
                        label: 'Background Color',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.button.borderRadius`}
                      field={{
                        type: 'number',
                        label: 'Border Radius',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mb: 2, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.button.padding`}
                      field={{
                        type: 'number',
                        label: 'Padding',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.button.gap`}
                      field={{
                        type: 'number',
                        label: 'Gap Between Buttons',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mb: 2, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.button.borderWidth`}
                      field={{
                        type: 'number',
                        label: 'Border Width',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardColorField
                      control={control}
                      fieldName={`${fieldName}.button.borderColor`}
                      field={{
                        type: 'color',
                        label: 'Border Color',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Button Hover State */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Button Hover State
                </Typography>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardColorField
                      control={control}
                      fieldName={`${fieldName}.button.hover.color`}
                      field={{
                        type: 'color',
                        label: 'Hover Text Color',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardColorField
                      control={control}
                      fieldName={`${fieldName}.button.hover.backgroundColor`}
                      field={{
                        type: 'color',
                        label: 'Hover Background Color',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardColorField
                      control={control}
                      fieldName={`${fieldName}.button.hover.borderColor`}
                      field={{
                        type: 'color',
                        label: 'Hover Border Color',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Container Styling */}
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Container Styling
                </Typography>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.container.margin`}
                      field={{
                        type: 'number',
                        label: 'Container Margin',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardSelectField
                      control={control}
                      fieldName={`${fieldName}.container.alignment`}
                      field={{
                        type: 'string',
                        label: 'Alignment',
                        enum: ['left', 'center', 'right'],
                        enumLabels: ['Left', 'Center', 'Right'],
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mt: 2, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardTextField
                      control={control}
                      fieldName={`${fieldName}.container.maxWidth`}
                      field={{
                        type: 'string',
                        label: 'Max Width (px or CSS)',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
                <Box sx={{ mt: 2, width: '100%' }}>
                  <AssetReferenceField
                    control={control}
                    fieldName={`${fieldName}.promptPrefixIconAssetID`}
                    field={{
                      type: 'reference',
                      label: 'Suggested prompt icon',
                      description: 'Optional JPEG, PNG, SVG, or WebP image shown before each suggested question (storefront embed and Conversation app).',
                      references: 'asset',
                      assetType: 'image',
                      defaultTags: '',
                      defaultPublicAccess: true,
                    }}
                    assetType="image"
                    readOnly={readOnly}
                  />
                </Box>
                <Box sx={{ mt: 2, width: '100%' }}>
                  <StandardNumberField
                    control={control}
                    fieldName={`${fieldName}.promptPrefixIconSize`}
                    field={{
                      type: 'number',
                      label: 'Suggested prompt icon size (px)',
                      description: 'Square icon: same width and height in pixels. Defaults to 16px when empty.',
                      defaultTags: '',
                      defaultPublicAccess: false,
                    }}
                    readOnly={readOnly}
                  />
                </Box>
              </Box>

              {/* Input: toggle + styling (non-scroll blocks only) */}
              <Box sx={{ mt: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Header Image
                </Typography>
                {fieldName === 'clientAIConfig.pageSuggestedPromptsStyle' && (
                  <Box sx={{ mb: 2 }}>
                    <AssetReferenceField
                      control={control}
                      fieldName="clientAIConfig.productAnswersHeaderImage"
                      field={{
                        type: 'reference',
                        label: 'Image',
                        description: 'Optional image shown above the Product Answers header.',
                        references: 'asset',
                        assetType: 'image',
                        defaultTags: '',
                        defaultPublicAccess: true,
                      }}
                      readOnly={readOnly}
                      assetType="image"
                    />
                    <Box sx={{ mt: 2 }}>
                      <StandardSelectField
                        control={control}
                        fieldName="clientAIConfig.productAnswersHeaderImageAlignment"
                        field={{
                          type: 'string',
                          label: 'Alignment',
                          enum: ['left', 'center', 'right'],
                          enumLabels: ['Left', 'Center', 'Right'],
                          defaultTags: '',
                          defaultPublicAccess: false,
                        }}
                        readOnly={readOnly}
                      />
                    </Box>
                  </Box>
                )}
                {fieldName === 'clientAIConfig.categoryPageSuggestedPromptsStyle' && (
                  <Box sx={{ mb: 2 }}>
                    <AssetReferenceField
                      control={control}
                      fieldName="clientAIConfig.categoryAnswersHeaderImage"
                      field={{
                        type: 'reference',
                        label: 'Image',
                        description: 'Optional image shown above the Category Answers header.',
                        references: 'asset',
                        assetType: 'image',
                        defaultTags: '',
                        defaultPublicAccess: true,
                      }}
                      readOnly={readOnly}
                      assetType="image"
                    />
                    <Box sx={{ mt: 2 }}>
                      <StandardSelectField
                        control={control}
                        fieldName="clientAIConfig.categoryAnswersHeaderImageAlignment"
                        field={{
                          type: 'string',
                          label: 'Alignment',
                          enum: ['left', 'center', 'right'],
                          enumLabels: ['Left', 'Center', 'Right'],
                          defaultTags: '',
                          defaultPublicAccess: false,
                        }}
                        readOnly={readOnly}
                      />
                    </Box>
                  </Box>
                )}
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Input
                </Typography>
                {fieldName === 'clientAIConfig.pageSuggestedPromptsStyle' && (
                  <Box sx={{ mb: 2 }}>
                    <StandardCheckboxField
                      control={control}
                      fieldName="clientAIConfig.productAnswersInputEnabled"
                      field={{
                        type: 'boolean',
                        label: 'Enable',
                        description: 'Show text input below prompts. Sends free-text to Shopping Assistant.',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                    <StandardTextField
                      control={control}
                      fieldName="clientAIConfig.productAnswersInputPlaceholder"
                      field={{
                        type: 'string',
                        label: 'Placeholder',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                )}
                {fieldName === 'clientAIConfig.categoryPageSuggestedPromptsStyle' && (
                  <Box sx={{ mb: 2 }}>
                    <StandardCheckboxField
                      control={control}
                      fieldName="clientAIConfig.categoryAnswersInputEnabled"
                      field={{
                        type: 'boolean',
                        label: 'Enable',
                        description: 'Show text input below prompts. Sends free-text to Shopping Assistant.',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                    <StandardTextField
                      control={control}
                      fieldName="clientAIConfig.categoryAnswersInputPlaceholder"
                      field={{
                        type: 'string',
                        label: 'Placeholder',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                )}
                <Stack direction="row" spacing={2} sx={{ mb: 2, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.input.fontSize`}
                      field={{
                        type: 'number',
                        label: 'Font Size',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardColorField
                      control={control}
                      fieldName={`${fieldName}.input.color`}
                      field={{
                        type: 'color',
                        label: 'Text Color',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mb: 2, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardColorField
                      control={control}
                      fieldName={`${fieldName}.input.backgroundColor`}
                      field={{
                        type: 'color',
                        label: 'Background Color',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.input.borderRadius`}
                      field={{
                        type: 'number',
                        label: 'Border Radius',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mb: 2, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.input.padding`}
                      field={{
                        type: 'number',
                        label: 'Padding',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.input.borderWidth`}
                      field={{
                        type: 'number',
                        label: 'Border Width',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardColorField
                      control={control}
                      fieldName={`${fieldName}.input.borderColor`}
                      field={{
                        type: 'color',
                        label: 'Border Color',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardColorField
                      control={control}
                      fieldName={`${fieldName}.input.focus.borderColor`}
                      field={{
                        type: 'color',
                        label: 'Focus Border Color',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
              </Box>
            </Box>
          )}
        />
        <HelperText
          name={fieldName}
          error={isTouched && error?.message}
          description={field.description}
        />
      </FormControl>
    )}
  />
);

const SuggestedPromptsScrollStyleControl = (
  control: Control,
  fieldName: string,
  field: FieldType,
  readOnly: boolean,
) => (
  <Controller
    name={fieldName}
    control={control}
    render={({
      field: {
        isTouched,
        error,
      },
    }: any) => (
      <FormControl sx={{ width: '100%' }}>
        <FormControlLabel
          label=""
          disabled={field.readOnly || readOnly}
          sx={{ width: '100%' }}
          control={(
            <Box
              sx={{
                width: '100%',
                border: '1px solid',
                borderColor: '#e0e0eb',
                borderRadius: 1,
                padding: 2,
                '&:hover': {
                  borderColor: '#111111',
                },
                '&:focus-within': {
                  border: '2px solid #1276dd',
                },
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Scroll Block Style
              </Typography>
              <Box sx={{ mb: 3, width: '100%' }}>
                <StandardSelectField
                  control={control}
                  fieldName={`${fieldName}.layout`}
                  field={{
                    type: 'string',
                    label: 'Scroll Direction',
                    enum: ['horizontal', 'vertical'],
                    enumLabels: ['Horizontal', 'Vertical'],
                    defaultTags: '',
                    defaultPublicAccess: false,
                  }}
                  readOnly={readOnly}
                />
              </Box>
              <Box sx={{ width: '100%' }}>
                <FormControlLabel
                  control={(
                    <Controller
                      name={`${fieldName}.scrollHideHeader`}
                      control={control}
                      render={({ field: { value, onChange } }: any) => (
                        <Checkbox
                          checked={!!value}
                          onChange={(e) => onChange(e.target.checked)}
                          disabled={readOnly}
                        />
                      )}
                    />
                  )}
                  label="Hide header"
                />
              </Box>
              <Box sx={{ mt: 2, width: '100%' }}>
                <StandardTextField
                  control={control}
                  fieldName={`${fieldName}.button.maxWidth`}
                  field={{
                    type: 'string',
                    label: 'Button max width',
                    defaultTags: '',
                    defaultPublicAccess: false,
                    placeholder: 'e.g. 220px, 14rem',
                  }}
                  readOnly={readOnly}
                />
              </Box>
              <Box sx={{ mt: 2, width: '100%' }}>
                <AssetReferenceField
                  control={control}
                  fieldName={`${fieldName}.promptPrefixIconAssetID`}
                  field={{
                    type: 'reference',
                    label: 'Suggested prompt icon (scroll block)',
                    description:
                      'Optional JPEG, PNG, SVG, or WebP image for scroll-style suggested prompts only. Product and category scroll blocks can each use a different icon. Leave empty to use the non-scroll icon, if any.',
                    references: 'asset',
                    assetType: 'image',
                    defaultTags: '',
                    defaultPublicAccess: true,
                  }}
                  assetType="image"
                  readOnly={readOnly}
                />
              </Box>
              <Box sx={{ mt: 2, width: '100%' }}>
                <StandardNumberField
                  control={control}
                  fieldName={`${fieldName}.promptPrefixIconSize`}
                  field={{
                    type: 'number',
                    label: 'Suggested prompt icon size (px, scroll block)',
                    description: 'Square icon: same width and height in pixels. Leave empty to inherit from non-scroll styles; otherwise defaults to 16px.',
                    defaultTags: '',
                    defaultPublicAccess: false,
                  }}
                  readOnly={readOnly}
                />
              </Box>
              <Box sx={{ mt: 2, width: '100%' }}>
                <StandardNumberField
                  control={control}
                  fieldName={`${fieldName}.autoScrollSpeed`}
                  field={{
                    type: 'number',
                    label: 'Auto-scroll speed (px/second)',
                    defaultTags: '',
                    defaultPublicAccess: false,
                  }}
                  readOnly={readOnly}
                />
              </Box>
            </Box>
          )}
        />
        <HelperText
          name={fieldName}
          error={isTouched && error?.message}
          description={field.description}
        />
      </FormControl>
    )}
  />
);

const SmartSummaryStyleControl = (
  control: Control,
  fieldName: string,
  field: FieldType,
  readOnly: boolean,
) => (
  <Controller
    name={fieldName}
    control={control}
    render={({
      field: {
        isTouched,
        error,
      },
    }: any) => (
      <FormControl sx={{ width: '100%' }}>
        <FormControlLabel
          label=""
          disabled={field.readOnly || readOnly}
          sx={{ width: '100%' }}
          control={(
            <Box
              sx={{
                width: '100%',
                border: '1px solid',
                borderColor: '#e0e0eb',
                borderRadius: 1,
                padding: 2,
                '&:hover': {
                  borderColor: '#111111',
                },
                '&:focus-within': {
                  border: '2px solid #1276dd',
                },
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Smart Summary Styling
              </Typography>

              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Summary / Bullets Styling
                </Typography>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.summary.fontSize`}
                      field={{
                        type: 'number',
                        label: 'Font Size',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardColorField
                      control={control}
                      fieldName={`${fieldName}.summary.color`}
                      field={{
                        type: 'color',
                        label: 'Color',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Container Styling
                </Typography>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.container.borderRadius`}
                      field={{
                        type: 'number',
                        label: 'Border Radius',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.container.borderWidth`}
                      field={{
                        type: 'number',
                        label: 'Border Width',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardColorField
                      control={control}
                      fieldName={`${fieldName}.container.borderColor`}
                      field={{
                        type: 'color',
                        label: 'Border Color',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ mt: 2, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardTextField
                      control={control}
                      fieldName={`${fieldName}.container.padding`}
                      field={{
                        type: 'string',
                        label: 'Container padding',
                        placeholder: '8',
                        description: 'Numbers are treated as px. CSS values allowed (e.g. 8px, 1rem).',
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
              </Box>
            </Box>
          )}
        />
        <HelperText
          name={fieldName}
          error={isTouched && error?.message}
          description={field.description}
        />
      </FormControl>
    )}
  />
);

const ProductCartConfigControl = (
  control: Control,
  fieldName: string,
  field: FieldType,
  readOnly: boolean,
) => (
  <Controller
    name={fieldName}
    control={control}
    defaultValue={{}}
    render={({
      field: {
        value,
        onChange,
        isTouched,
        error,
      },
    }: any) => (
      <FormControl
        sx={{ width: '100%' }}
      >
        <FormControlLabel
          label=""
          disabled={field.readOnly || readOnly}
          sx={{ width: '100%' }}
          control={(
            <Box
              sx={{
                width: '100%',
                border: '1px solid',
                borderColor: '#e0e0eb',
                borderRadius: 1,
                padding: 2,
                '&:hover': {
                  borderColor: '#111111',
                },
                '&:focus-within': {
                  border: '2px solid #1276dd',
                },
              }}
            >
              <EnhancedSection
                readOnly={readOnly}
                value={value}
                onChange={onChange}
              />

              <Typography variant="h6" sx={{ mb: 2 }}>
                Add To Cart
              </Typography>

              <AddToCartSection
                control={control}
                fieldName={fieldName}
                readOnly={readOnly}
              />

              <AddToCartButtonStyleSection
                control={control}
                fieldName={fieldName}
                readOnly={readOnly}
              />

              <Typography variant="h6" sx={{ mb: 2 }}>
                Call to Action Button
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Shown in place of Add to Cart when Add to Cart is off. Uses token label.
                </Typography>
                <FormControlLabel
                  label="Show CTA button"
                  disabled={readOnly}
                  control={(
                    <Checkbox
                      checked={value?.showCTAButton ?? false}
                      onChange={(e) => onChange({
                        ...value,
                        showCTAButton: e.target.checked,
                      })}
                      name={`${fieldName}.showCTAButton`}
                    />
                  )}
                />
              </Box>

              {/* Image Swatches Configuration */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Variant Image Swatches
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    label="Enable Image Swatches"
                    disabled={readOnly}
                    control={(
                      <Checkbox
                        checked={value?.imageSwatches?.enabled ?? true}
                        onChange={(e) => onChange({
                          ...value,
                          imageSwatches: {
                            ...(value?.imageSwatches || {}),
                            enabled: e.target.checked,
                          },
                        })}
                        name={`${fieldName}.imageSwatches.enabled`}
                      />
                    )}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary', ml: 4 }}>
                    Show variant images as clickable thumbnails in Enhanced Cards
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <StandardNumberField
                    control={control}
                    fieldName={`${fieldName}.imageSwatches.size`}
                    field={{
                      type: 'number',
                      label: 'Swatch Size (px)',
                      defaultTags: '',
                      defaultPublicAccess: false,
                      default: 50,
                    }}
                    readOnly={readOnly}
                  />
                  <StandardNumberField
                    control={control}
                    fieldName={`${fieldName}.imageSwatches.gap`}
                    field={{
                      type: 'number',
                      label: 'Gap Between Swatches',
                      defaultTags: '',
                      defaultPublicAccess: false,
                      default: 8,
                    }}
                    readOnly={readOnly}
                  />
                </Stack>
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    label="Hide Out of Stock Variants"
                    disabled={readOnly}
                    control={(
                      <Checkbox
                        checked={value?.imageSwatches?.hideOnOutOfStock ?? true}
                        onChange={(e) => onChange({
                          ...value,
                          imageSwatches: {
                            ...(value?.imageSwatches || {}),
                            hideOnOutOfStock: e.target.checked,
                          },
                        })}
                        name={`${fieldName}.imageSwatches.hideOnOutOfStock`}
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* Enable Variant Pills */}
              {/* TODO: add more variants */}
              {/* <Box sx={{ mb: 3, width: '100%' }}>
                <FormControlLabel
                  label="Enable Variant Pills"
                  disabled={field.readOnly || readOnly}
                  control={(
                    <Checkbox
                      checked={value?.variantPillsEnabled || false}
                      onChange={(e) => onChange({
                        ...value,
                        variantPillsEnabled: e.target.checked,
                      })}
                      name={`${fieldName}.variantPillsEnabled`}
                    />
                  )}
                />
              </Box> */}

              <PostPurchaseSection
                fieldName={fieldName}
                readOnly={readOnly}
                value={value}
                onChange={onChange}
              />

              {/* Success Message */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <StandardTextField
                  control={control}
                  fieldName={`${fieldName}.successMessage`}
                  field={{
                    type: 'string',
                    label: 'Success Message',
                    defaultTags: '',
                    defaultPublicAccess: false,
                  }}
                  readOnly={readOnly}
                />
              </Box>

              {/* Checkout Button Text */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <StandardTextField
                  control={control}
                  fieldName={`${fieldName}.checkoutButtonText`}
                  field={{
                    type: 'string',
                    label: 'Checkout Button Text',
                    defaultTags: '',
                    defaultPublicAccess: false,
                  }}
                  readOnly={readOnly}
                />
              </Box>

              {/* Keep Shopping Button Text */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <StandardTextField
                  control={control}
                  fieldName={`${fieldName}.keepShoppingButtonText`}
                  field={{
                    type: 'string',
                    label: 'Keep Shopping Button Text',
                    defaultTags: '',
                    defaultPublicAccess: false,
                  }}
                  readOnly={readOnly}
                />
              </Box>

              {/* Continue Conversation Text */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <StandardTextField
                  control={control}
                  fieldName={`${fieldName}.continueConversationText`}
                  field={{
                    type: 'string',
                    label: 'Continue Conversation Text',
                    defaultTags: '',
                    defaultPublicAccess: false,
                  }}
                  readOnly={readOnly}
                />
              </Box>

              {/* Continue Conversation Button Text */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <StandardTextField
                  control={control}
                  fieldName={`${fieldName}.continueConversationButtonText`}
                  field={{
                    type: 'string',
                    label: 'Continue Conversation Button Text',
                    defaultTags: '',
                    defaultPublicAccess: false,
                  }}
                  readOnly={readOnly}
                />
              </Box>

              {/* Checkout URL */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <StandardTextField
                  control={control}
                  fieldName={`${fieldName}.checkoutUrl`}
                  field={{
                    type: 'string',
                    label: 'Checkout URL',
                    defaultTags: '',
                    defaultPublicAccess: false,
                    default: '/checkout',
                  }}
                  readOnly={readOnly}
                />
              </Box>
            </Box>
          )}
        />
        <HelperText
          name={fieldName}
          error={isTouched && error?.message}
          description={field.description}
        />
      </FormControl>
    )}
  />
);

const DualModeCardOrderEditor = ({
  value,
  onChange,
}: {
  value: any;
  onChange: (v: any) => void;
}) => {
  const { currentPortal: portal } = useStore();
  const dualModeEnabled = Boolean(portal?.clientAIConfig?.clientAIUseDualSearch);

  return (
    <Box
      sx={{
        mb: 2,
        width: '100%',
        opacity: dualModeEnabled ? 1 : 0.5,
        pointerEvents: dualModeEnabled ? 'auto' : 'none',
      }}
    >
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
        Element Order (Drag to reorder)
      </Typography>
      <Box sx={{ border: '1px solid #e0e0eb', borderRadius: 1, p: 2 }}>
        <ProductCardOrderEditor
          value={value}
          onChange={onChange}
        />
      </Box>
    </Box>
  );
};

const AiSearchConfigControl = (
  control: Control,
  fieldName: string,
  field: FieldType,
  readOnly: boolean,
) => (
  <Controller
    name={fieldName}
    control={control}
    defaultValue={{}}
    render={({
      field: {
        value,
        onChange,
        isTouched,
        error,
      },
    }: any) => (
      <FormControl sx={{ width: '100%' }}>
        <FormControlLabel
          label=""
          disabled={field.readOnly || readOnly}
          sx={{ width: '100%' }}
          control={(
            <Box
              sx={{
                width: '100%',
                border: '1px solid',
                borderColor: '#e0e0eb',
                borderRadius: 1,
                padding: 2,
                '&:hover': {
                  borderColor: '#111111',
                },
                '&:focus-within': {
                  border: '2px solid #1276dd',
                },
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                AI Search Styling
              </Typography>

              {/* Main Image Section */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Main Image
                </Typography>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardTextField
                      control={control}
                      fieldName={`${fieldName}.mainImage.height`}
                      field={{
                        type: 'string',
                        label: 'Height',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: '50vh',
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardTextField
                      control={control}
                      fieldName={`${fieldName}.mainImage.minHeight`}
                      field={{
                        type: 'string',
                        label: 'Min Height',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: '200px',
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.mainImage.aspectRatio`}
                      field={{
                        type: 'number',
                        label: 'Aspect Ratio',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 0.775,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Thumbnails Section */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Thumbnails
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mb: 2, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.thumbnails.width`}
                      field={{
                        type: 'number',
                        label: 'Width (px)',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 76,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.thumbnails.height`}
                      field={{
                        type: 'number',
                        label: 'Height (px)',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 76,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.thumbnails.borderRadius`}
                      field={{
                        type: 'number',
                        label: 'Border Radius',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 8,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Variant Photo Swatches Section */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Variant Photo Swatches
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    label="Enable Variant Photo Swatches"
                    disabled={readOnly}
                    control={(
                      <Checkbox
                        checked={value?.variantPhotoSwatches?.enabled || false}
                        onChange={(e) => onChange({
                          ...value,
                          variantPhotoSwatches: {
                            ...(value?.variantPhotoSwatches || {}),
                            enabled: e.target.checked,
                          },
                        })}
                        name={`${fieldName}.variantPhotoSwatches.enabled`}
                      />
                    )}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary', ml: 4 }}>
                    Show variant images as clickable thumbnails that change the main image
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.variantPhotoSwatches.size`}
                      field={{
                        type: 'number',
                        label: 'Swatch Size (px)',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 50,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.variantPhotoSwatches.gap`}
                      field={{
                        type: 'number',
                        label: 'Gap Between Swatches',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 8,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Typography Section */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Typography
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mb: 2, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardTextField
                      control={control}
                      fieldName={`${fieldName}.typography.titleFontSize`}
                      field={{
                        type: 'string',
                        label: 'Title Font Size',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: '1.1rem',
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardTextField
                      control={control}
                      fieldName={`${fieldName}.typography.priceFontSize`}
                      field={{
                        type: 'string',
                        label: 'Price Font Size',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: '1rem',
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardTextField
                      control={control}
                      fieldName={`${fieldName}.typography.errorFontSize`}
                      field={{
                        type: 'string',
                        label: 'Error Font Size',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: '0.75rem',
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Description Section */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Description
                </Typography>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.description.cardMaxLength`}
                      field={{
                        type: 'number',
                        label: 'Card Max Length',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 75,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.description.panelMaxLength`}
                      field={{
                        type: 'number',
                        label: 'Panel Max Length',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 200,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <FormControlLabel
                      label="Show See More"
                      disabled={readOnly}
                      control={(
                        <Checkbox
                          checked={value?.description?.showSeeMore ?? true}
                          onChange={(e) => onChange({
                            ...value,
                            description: {
                              ...(value?.description || {}),
                              showSeeMore: e.target.checked,
                            },
                          })}
                          name={`${fieldName}.description.showSeeMore`}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Details Button Section */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Details Button
                </Typography>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardTextField
                      control={control}
                      fieldName={`${fieldName}.detailsButton.padding`}
                      field={{
                        type: 'string',
                        label: 'Padding',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: '12px 16px',
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.detailsButton.borderRadius`}
                      field={{
                        type: 'number',
                        label: 'Border Radius',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 8,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardTextField
                      control={control}
                      fieldName={`${fieldName}.detailsButton.fontSize`}
                      field={{
                        type: 'string',
                        label: 'Font Size',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: '0.875rem',
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Layout Section */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Layout Configuration
                </Typography>
                <Stack direction="row" spacing={2} sx={{ width: '100%', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardTextField
                      control={control}
                      fieldName={`${fieldName}.layout.headerHeightValue`}
                      field={{
                        type: 'string',
                        label: 'Header Height Value',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: '88px',
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardTextField
                      control={control}
                      fieldName={`${fieldName}.layout.headerCssVariable`}
                      field={{
                        type: 'string',
                        label: 'CSS Variable Name',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: '--header-height',
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <FormControlLabel
                      label="Use CSS Variable"
                      disabled={readOnly}
                      control={(
                        <Checkbox
                          checked={value?.layout?.useCssVariable ?? true}
                          onChange={(e) => onChange({
                            ...value,
                            layout: {
                              ...(value?.layout || {}),
                              useCssVariable: e.target.checked,
                            },
                          })}
                          name={`${fieldName}.layout.useCssVariable`}
                        />
                      )}
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.layout.initialHeight`}
                      field={{
                        type: 'number',
                        label: 'Initial Height (px)',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 500,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.layout.inputHeight`}
                      field={{
                        type: 'number',
                        label: 'Input Height (px)',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 120,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.layout.paddingBottom`}
                      field={{
                        type: 'number',
                        label: 'Padding Bottom (px)',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 50,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.layout.minExpandedHeight`}
                      field={{
                        type: 'number',
                        label: 'Min Expanded Height (px)',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 800,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Image Configuration Section */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Image Configuration
                </Typography>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.mainImage.maxHeight`}
                      field={{
                        type: 'number',
                        label: 'Max Image Height (px)',
                        default: 250,
                        defaultTags: '',
                        defaultPublicAccess: false,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardSelectField
                      control={control}
                      fieldName={`${fieldName}.mainImage.objectFit`}
                      field={{
                        type: 'string',
                        label: 'Image Fit',
                        enum: ['contain', 'cover'],
                        enumLabels: ['Contain (show full image)', 'Cover (crop to fit)'],
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 'cover',
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Dual Mode Card Configuration Section */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Dual Mode Card Configuration
                </Typography>
                <Stack direction="row" spacing={2} sx={{ width: '100%', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.dualModeCardConfig.maxHeight`}
                      field={{
                        type: 'number',
                        label: 'Max Height (px)',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 200,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardSelectField
                      control={control}
                      fieldName={`${fieldName}.dualModeCardConfig.orientation`}
                      field={{
                        type: 'string',
                        label: 'Orientation',
                        enum: ['vertical', 'horizontal'],
                        enumLabels: ['Vertical', 'Horizontal'],
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 'vertical',
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardSelectField
                      control={control}
                      fieldName={`${fieldName}.dualModeCardConfig.productCardVariant`}
                      field={{
                        type: 'string',
                        label: 'Card Variant',
                        enum: ['minimal', 'withPrice'],
                        enumLabels: ['Minimal', 'With Price'],
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 'minimal',
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
                {/* Element Order with Checkboxes - Using ProductCardOrderEditor */}
                <DualModeCardOrderEditor
                  value={value?.dualModeCardConfig || {}}
                  onChange={(newValue) => onChange({
                    ...value,
                    dualModeCardConfig: {
                      ...(value?.dualModeCardConfig || {}),
                      ...newValue,
                    },
                  })}
                />
              </Box>

              {/* Dual Mode Top Section Configuration */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Dual Mode Top Section
                </Typography>
                <Stack direction="row" spacing={2} sx={{ width: '100%', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.topSection.maxHeight`}
                      field={{
                        type: 'number',
                        label: 'Top Section Max Height - AI Mode (px)',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 400,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ width: '100%', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardTextField
                      control={control}
                      fieldName={`${fieldName}.topSection.seeMoreLabel`}
                      field={{
                        type: 'string',
                        label: 'See More Label',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 'See more results',
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardTextField
                      control={control}
                      fieldName={`${fieldName}.topSection.seeLessLabel`}
                      field={{
                        type: 'string',
                        label: 'See Less Label',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 'See less',
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Frame Border Configuration */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Frame Border
                </Typography>
                <Stack direction="row" spacing={2} sx={{ width: '100%', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.topSection.frameBorder.width`}
                      field={{
                        type: 'number',
                        label: 'Border Width (px)',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 1,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardColorField
                      control={control}
                      fieldName={`${fieldName}.topSection.frameBorder.color`}
                      field={{
                        type: 'color',
                        label: 'Border Color',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: '#e5e7eb',
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StandardNumberField
                      control={control}
                      fieldName={`${fieldName}.topSection.frameBorder.radius`}
                      field={{
                        type: 'number',
                        label: 'Border Radius (px)',
                        defaultTags: '',
                        defaultPublicAccess: false,
                        default: 8,
                      }}
                      readOnly={readOnly}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Personalization Section */}
              <Box sx={{ mb: 3, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Personalization
                </Typography>
                <Stack direction="row" spacing={2} sx={{ width: '100%', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={value?.personalization?.enabled || false}
                          onChange={(e) => {
                            onChange({
                              ...value,
                              personalization: {
                                ...value?.personalization,
                                enabled: e.target.checked,
                              },
                            });
                          }}
                          disabled={readOnly}
                        />
                      )}
                      label="Enable Product Personalization"
                    />
                  </Box>
                </Stack>
                {value?.personalization?.enabled && (
                  <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
                    <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                      <Box sx={{ flex: 1 }}>
                        <StandardTextField
                          control={control}
                          fieldName={`${fieldName}.personalization.personalizeButtonText`}
                          field={{
                            type: 'string',
                            label: 'Personalize Button Text',
                            defaultTags: '',
                            defaultPublicAccess: false,
                            default: 'Click to Personalize It',
                          }}
                          readOnly={readOnly}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <StandardTextField
                          control={control}
                          fieldName={`${fieldName}.personalization.addToCartWithoutPersonalizationText`}
                          field={{
                            type: 'string',
                            label: 'Add to Cart without Personalization Text',
                            defaultTags: '',
                            defaultPublicAccess: false,
                            default: 'Add to Cart without Personalization',
                          }}
                          readOnly={readOnly}
                        />
                      </Box>
                    </Stack>
                  </Stack>
                )}
              </Box>
            </Box>
          )}
        />
        <HelperText
          name={fieldName}
          error={isTouched && error?.message}
          description={field.description}
        />
      </FormControl>
    )}
  />
);

// Legacy Suggested Prompts Control (shows only when localization is DISABLED)
const LegacySuggestedPromptsControl = (
  control: Control,
  fieldName: string,
  field: FieldType,
  readOnly: boolean,
) => {
  // Read from clientAIConfig.clientAISuggestedPromptsLocalized
  const clientAIConfig = useWatch({ control, name: 'clientAIConfig' });
  const isLocalized = clientAIConfig?.clientAISuggestedPromptsLocalized || false;

  // Hide when localization is enabled (replaced by L10N control)
  if (isLocalized) {
    return null;
  }

  return (
    <StandardBulletPointsField
      control={control}
      fieldName={fieldName}
      field={field}
      readOnly={readOnly}
    />
  );
};

// Legacy Persistent Prompts Control (shows only when localization is DISABLED)
const LegacyPersistentPromptsControl = (
  control: Control,
  fieldName: string,
  field: FieldType,
  readOnly: boolean,
) => {
  // Read from clientAIConfig.clientAIPersistentPromptsLocalized
  const clientAIConfig = useWatch({ control, name: 'clientAIConfig' });
  const isLocalized = clientAIConfig?.clientAIPersistentPromptsLocalized || false;

  // Hide when localization is enabled (replaced by L10N control)
  if (isLocalized) {
    return null;
  }

  return (
    <StandardBulletPointsField
      control={control}
      fieldName={fieldName}
      field={field}
      readOnly={readOnly}
    />
  );
};

// Localized Suggested Prompts Control (shows only when localization is ENABLED)
const LocalizedSuggestedPromptsL10NControl = (
  control: Control,
  fieldName: string,
  field: FieldType,
  readOnly: boolean,
) => {
  // Read from clientAIConfig.clientAISuggestedPromptsLocalized
  const isLocalized = useWatch({
    control,
    name: 'clientAIConfig.clientAISuggestedPromptsLocalized',
  }) || false;

  if (!isLocalized) {
    return null;
  }

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field: { onChange, value } }) => (
        <LocalizedTextArrayControl
          label="Suggested Prompts (Localized)"
          value={value as Record<string, string> | undefined}
          onChange={onChange}
          disabled={readOnly}
          placeholder="Add prompt..."
        />
      )}
    />
  );
};

// Localized Persistent Prompts Control (shows only when localization is ENABLED)
const LocalizedPersistentPromptsL10NControl = (
  control: Control,
  fieldName: string,
  field: FieldType,
  readOnly: boolean,
) => {
  // Read from clientAIConfig.clientAIPersistentPromptsLocalized
  const isLocalized = useWatch({
    control,
    name: 'clientAIConfig.clientAIPersistentPromptsLocalized',
  }) || false;

  if (!isLocalized) {
    return null;
  }

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field: { onChange, value } }) => (
        <LocalizedTextControl
          label="Persistent Prompts (Localized)"
          value={value as Record<string, string> | undefined}
          onChange={onChange}
          disabled={readOnly}
          placeholder="Add persistent prompt..."
        />
      )}
    />
  );
};

// Checkbox controls that read/write to clientAIConfig
const SuggestedPromptsLocalizedCheckboxControl = (
  control: Control,
  fieldName: string,
  field: FieldType,
  readOnly: boolean,
) => (
  <StandardCheckboxField
    control={control}
    fieldName={fieldName}
    field={field}
    readOnly={readOnly}
  />
);

const PersistentPromptsLocalizedCheckboxControl = (
  control: Control,
  fieldName: string,
  field: FieldType,
  readOnly: boolean,
) => (
  <StandardCheckboxField
    control={control}
    fieldName={fieldName}
    field={field}
    readOnly={readOnly}
  />
);

const ProductStructuredDataConfigControlType = (
  control: Control,
  fieldName: string,
  _field: FieldType,
  readOnly: boolean,
) => (
  <ProductStructuredDataConfigControl
    control={control}
    fieldName={fieldName}
    readOnly={readOnly}
  />
);

const normalizeGuideProductPopupConfig = (value: any) => {
  const safeValue = { ...(value || {}) };
  delete (safeValue as Record<string, unknown>).pageTargeting;
  return ({
    ...GUIDE_PRODUCT_POPUP_DEFAULT_CONFIG,
    ...safeValue,
    autoOpen: {
      ...GUIDE_PRODUCT_POPUP_DEFAULT_CONFIG.autoOpen,
      ...(value?.autoOpen || {}),
    },
    size: {
      ...GUIDE_PRODUCT_POPUP_DEFAULT_CONFIG.size,
      ...(value?.size || {}),
    },
    content: {
      ...GUIDE_PRODUCT_POPUP_DEFAULT_CONFIG.content,
      ...(value?.content || {}),
    },
    products: {
      ...GUIDE_PRODUCT_POPUP_DEFAULT_CONFIG.products,
      ...(value?.products || {}),
    },
  });
};

const GuideProductPopupConfigControlType = (
  control: Control,
  fieldName: string,
  _field: FieldType,
  readOnly: boolean,
) => (
  <Controller
    name={fieldName}
    control={control}
    render={({ field: { onChange, value } }) => {
      const config = normalizeGuideProductPopupConfig(value);
      const update = (patch: any) => onChange(normalizeGuideProductPopupConfig({
        ...config,
        ...patch,
      }));
      const updateNested = (
        key: 'autoOpen' | 'content' | 'products' | 'size',
        patch: any,
      ) => {
        update({
          [key]: {
            ...(config as any)[key],
            ...patch,
          },
        });
      };

      return (
        <Stack spacing={2}>
          <FormControlLabel
            control={(
              <Switch
                checked={Boolean(config.enabled)}
                onChange={(event) => update({ enabled: event.target.checked })}
                disabled={readOnly}
              />
            )}
            label="Enable Related Products Popup"
          />
          <TextField
            label="Heading"
            value={config.content.heading || ''}
            onChange={(event) => updateNested('content', { heading: event.target.value })}
            disabled={readOnly}
            fullWidth
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Width (px)"
              type="number"
              value={config.size.widthPx ?? ''}
              onChange={(event) => {
                const widthPx = Number(event.target.value);
                updateNested('size', {
                  widthPx: event.target.value !== '' && Number.isFinite(widthPx) && widthPx > 0
                    ? Math.min(Math.floor(widthPx), 4096)
                    : null,
                });
              }}
              helperText="Leave blank to use the responsive theme default."
              inputProps={{ min: 1, max: 4096, step: 1 }}
              disabled={readOnly}
              fullWidth
            />
            <TextField
              label="Height (px)"
              type="number"
              value={config.size.heightPx ?? ''}
              onChange={(event) => {
                const heightPx = Number(event.target.value);
                updateNested('size', {
                  heightPx: event.target.value !== '' && Number.isFinite(heightPx) && heightPx > 0
                    ? Math.min(Math.floor(heightPx), 4096)
                    : null,
                });
              }}
              helperText="Leave blank to use the full viewport height."
              inputProps={{ min: 1, max: 4096, step: 1 }}
              disabled={readOnly}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Open Delay (ms)"
              type="number"
              value={config.autoOpen.delayMs ?? 0}
              onChange={(event) => updateNested('autoOpen', {
                delayMs: Math.max(0, Number(event.target.value) || 0),
              })}
              inputProps={{ min: 0, max: 60000, step: 1 }}
              disabled={readOnly}
              fullWidth
            />
            <TextField
              label="Max Products"
              type="number"
              value={config.products.maxItems ?? 12}
              onChange={(event) => updateNested('products', {
                maxItems: Math.max(1, Math.min(12, Number(event.target.value) || 12)),
              })}
              inputProps={{ min: 1, max: 12, step: 1 }}
              disabled={readOnly}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Add-to-Cart Label"
              value={config.content.addToCartLabel || ''}
              onChange={(event) => updateNested('content', { addToCartLabel: event.target.value })}
              disabled={readOnly}
              fullWidth
            />
            <TextField
              label="Variant Label"
              value={config.content.variantLabel || ''}
              onChange={(event) => updateNested('content', { variantLabel: event.target.value })}
              disabled={readOnly}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Adding Label"
              value={config.content.addingLabel || ''}
              onChange={(event) => updateNested('content', { addingLabel: event.target.value })}
              disabled={readOnly}
              fullWidth
            />
            <TextField
              label="Added Label"
              value={config.content.addedLabel || ''}
              onChange={(event) => updateNested('content', { addedLabel: event.target.value })}
              disabled={readOnly}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Unavailable Label"
              value={config.content.unavailableLabel || ''}
              onChange={(event) => updateNested('content', {
                unavailableLabel: event.target.value,
              })}
              disabled={readOnly}
              fullWidth
            />
            <TextField
              label="Retry Label"
              value={config.content.retryLabel || ''}
              onChange={(event) => updateNested('content', { retryLabel: event.target.value })}
              disabled={readOnly}
              fullWidth
            />
          </Stack>
          <TextField
            label="Error Message"
            value={config.content.errorLabel || ''}
            onChange={(event) => updateNested('content', { errorLabel: event.target.value })}
            disabled={readOnly}
            fullWidth
          />
        </Stack>
      );
    }}
  />
);

const instructionControl = (c: Control, fn: string, f: FieldWithPromptKeyType, ro: boolean) => (
  <InstructionFieldWithPromptModel
    control={c}
    fieldName={fn}
    field={f}
    readOnly={ro as boolean}
  />
);

const overrides = {
  // Checkbox controls (read/write to clientAIConfig)
  'clientAIConfig.clientAISuggestedPromptsLocalized': SuggestedPromptsLocalizedCheckboxControl,
  'clientAIConfig.clientAIPersistentPromptsLocalized': PersistentPromptsLocalizedCheckboxControl,
  // Legacy controls (visible when localization is OFF)
  clientAISuggestedPrompts: LegacySuggestedPromptsControl,
  clientAIPersistentPrompts: LegacyPersistentPromptsControl,
  // Localized controls (visible when localization is ON)
  'clientAIConfig.clientAISuggestedPromptsL10N': LocalizedSuggestedPromptsL10NControl,
  'clientAIConfig.clientAIPersistentPromptsL10N': LocalizedPersistentPromptsL10NControl,
  clientAIAnswerLength: AnswerLengthControlType,
  'clientAIConfig.pageGeneratedContentHeader': PageGeneratedContentHeaderControl,
  'clientAIConfig.pageSuggestedPromptsStyle': SuggestedPromptsStyleControl,
  'clientAIConfig.pageSuggestedPromptsScrollStyle': SuggestedPromptsScrollStyleControl,
  'clientAIConfig.categoryPageSuggestedPromptsStyle': SuggestedPromptsStyleControl,
  'clientAIConfig.categoryPageSuggestedPromptsScrollStyle': SuggestedPromptsScrollStyleControl,
  'clientAIConfig.pageSmartSummaryStyle': SmartSummaryStyleControl,
  'clientAIConfig.productStructuredDataConfig': ProductStructuredDataConfigControlType,
  'clientAIConfig.productCardConfig': ProductCartConfigControl,
  'clientAIConfig.aiSearchConfig': AiSearchConfigControl,
  'clientAIConfig.guideProductPopupConfig': GuideProductPopupConfigControlType,
  // Instruction fields: "i" opens read-only modal with base (override or default)
  'clientAIConfig.shoppingAssistantInstructions': instructionControl,
  'clientAIConfig.aiModeSearchInstructions': instructionControl,
  'clientAIConfig.dualSearchInstructions': instructionControl,
  'clientAIConfig.orderStatusToolInstructions': instructionControl,
  'clientAIConfig.exitToolInstructions': instructionControl,
  'clientAIConfig.featureCaptureToolInstructions': instructionControl,
  'clientAIConfig.productAnswersInstructions': instructionControl,
  'clientAIConfig.categoryAnswersInstructions': instructionControl,
  'clientAIConfig.hamburgerMenu': HamburgerMenuLinksControl,
  // clientAIConfig: (control, fieldName, field, readOnly) => (
  //   <ClientAIConfigOverride control={control} readOnly={readOnly} />
  // ),
  // 'clientAIConfig.openSupportToolOpenHours': OpenHoursControlType,
};

const formConfig = [
  {
    name: 'tabs',
    type: 'tabs',
    elements: [
      {
        name: 'general',
        type: 'group',
        label: 'General',
        elements: [
          {
            name: 'integration',
            label: 'Integration',
            type: 'subGroup',
            elements: [
              'clientAIBaseUrl',
              'clientAIConfig.useGoogleAnalytics',
              'clientAIConfig.googleAnalyticsPropertyID',
            ],
          },
          {
            name: 'promptGuidance',
            label: 'Prompt Guidance',
            type: 'subGroup',
            elements: [
              'clientAIConfig.brandVoicePrompt',
              'clientAIAnswerLength',
              'clientAIAdditionalInstructions',
            ],
          },
          {
            name: 'display',
            label: 'Display',
            type: 'subGroup',
            elements: [
              'clientAIInlineReferences',
              'clientAISuggestRefinements',
              'clientAIFeedbackButtons',
              // TODO: remove this MCN-2144 (move to tokens)
              'clientAIStartingMessage',
              'clientAIPromptPlaceholder',
              // Suggested Prompts: checkbox first, then legacy OR localized
              'clientAIConfig.clientAISuggestedPromptsLocalized',
              'clientAISuggestedPrompts',
              'clientAIConfig.clientAISuggestedPromptsL10N',
              // Persistent Prompts: checkbox first, then legacy OR localized
              'clientAIConfig.clientAIPersistentPromptsLocalized',
              'clientAIPersistentPrompts',
              'clientAIConfig.clientAIPersistentPromptsL10N',
            ],
          },
          {
            name: 'ecommerceFeatures',
            label: 'E-commerce Features',
            type: 'subGroup',
            elements: [
              'clientAIConfig.mergeProductIntegrationData',
              'clientAIConfig.enhancedProductCardEnabled',
              'clientAIConfig.addToCartEnabled',
              'clientAIConfig.enableGeoLocation',
              'clientAIConfig.clientAIUseDualSearch',
              // 'clientAIConfig.aiSearchFullPageEnabled', // TODO: add admin config
              'clientAIConfig.productCardConfig',
              'clientAIConfig.aiSearchConfig',
            ],
          },
          {
            name: 'modelSettings',
            label: 'Model Settings',
            type: 'subGroup',
            elements: [
              'clientAIConfig.languageModel',
              'clientAIConfig.temperature',
            ],
          },
          {
            name: 'hamburgerMenu',
            label: 'Hamburger Menu',
            type: 'subGroup',
            elements: [
              'clientAIConfig.hamburgerMenu',
            ],
          },
        ],
      },
      {
        name: 'geo',
        type: 'group',
        label: 'GEO',
        elements: [
          {
            name: 'relatedProductsPopup',
            label: 'Related Products Popup',
            type: 'subGroup',
            elements: [
              'clientAIConfig.guideProductPopupConfig',
            ],
          },
        ],
      },
      {
        name: 'tools',
        type: 'group',
        label: 'Tools',
        elements: [
          {
            name: 'knowledgeBaseTool',
            label: 'Knowledge Base Tool',
            type: 'subGroup',
            elements: [
              'clientAIConfig.knowledgeBaseToolEnabled',
              'clientAIConfig.maxResults',
              'clientAIConfig.useProductType',
              'clientAIConfig.useFullProductSheet',
              'clientAIConfig.filterMode',
            ],
          },
          {
            name: 'openSupportTool',
            label: 'Open Support Tool',
            type: 'subGroup',
            elements: [
              'clientAIConfig.openSupportToolEnabled',
              'clientAIConfig.openSupportToolStatus',
              'clientAIConfig.openSupportToolOpenHours',
              'clientAIConfig.openSupportToolTimezone',
              'clientAIConfig.openSupportToolMessage',
              'clientAIConfig.openSupportToolOffHoursMessage',
              'clientAIConfig.openSupportToolOpenHoursPrompt',
              'clientAIConfig.openSupportToolOffHoursPrompt',
              'clientAIConfig.openSupportToolDelay',
            ],
          },
          {
            name: 'orderStatusTool',
            label: 'Order Status Tool',
            type: 'subGroup',
            elements: [
              'clientAIConfig.orderStatusToolEnabled',
              'clientAIConfig.orderStatusToolInstructions',
            ],
          },
          {
            name: 'exitTool',
            label: 'Exit Conversation Tool',
            type: 'subGroup',
            elements: [
              'clientAIConfig.exitToolEnabled',
              'clientAIConfig.exitToolInstructions',
            ],
          },
          {
            name: 'featureCaptureTool',
            label: 'Feature Capture Tool',
            type: 'subGroup',
            elements: [
              'clientAIConfig.featureCaptureToolEnabled',
              'clientAIConfig.featureCaptureConcepts',
              'clientAIConfig.featureCaptureToolInstructions',
            ],
          },
          {
            name: 'followUpRequestTool',
            label: 'Follow-up Request Tool',
            type: 'subGroup',
            elements: [
              'clientAIConfig.followUpRequestToolEnabled',
              'clientAIConfig.followUpNotificationEmail',
              'clientAIConfig.followUpRequestToolInstructions',
            ],
          },
          {
            name: 'pageGeneratedContentHeader',
            label: 'Page-generated content header',
            type: 'subGroup',
            elements: [
              'clientAIConfig.pageGeneratedContentHeader',
            ],
          },
          {
            name: 'productAnswers',
            label: 'Product Answers',
            type: 'subGroup',
            elements: [
              'clientAIConfig.productPageSuggestedPromptsEnabled',
              'clientAIConfig.pageSuggestedPromptsMaxCount',
              'clientAIConfig.pageSuggestedPromptsRefreshDays',
              'clientAIConfig.pageSuggestedPromptsHeader',
              'clientAIConfig.welcomeScreenHtml',
              'clientAIConfig.suppressSuggestedPromptMessage',
              'clientAIConfig.hideProductAnswerTitleInMessage',
              'clientAIConfig.resetConversationMode',
              'clientAIConfig.pageSuggestedPromptsStyle',
              'clientAIConfig.pageSuggestedPromptsScrollStyle',
              'clientAIConfig.pageSuggestedPromptsCustomCss',
              'clientAIConfig.productContentInstructions',
              'clientAIConfig.showProductPromptsInShoppingAssistant',
              'clientAIConfig.displayProductQuestionsAtConversationEnd',
            ],
          },
          {
            name: 'askAboutProductWidget',
            label: 'Ask About this Product Widget',
            type: 'subGroup',
            elements: [
              'clientAIConfig.askAboutProductWidgetEnabled',
              'clientAIConfig.askAboutProductWidgetHeaderImage',
              'clientAIConfig.askAboutProductWidgetHeaderImageAlignment',
              'clientAIConfig.askAboutProductWidgetHeaderTitle',
              'clientAIConfig.askAboutProductWidgetHeaderSubtitle',
              'clientAIConfig.askAboutProductWidgetFooterText',
              'clientAIConfig.askAboutProductWidgetInputPlaceholder',
              'clientAIConfig.askAboutProductWidgetSubmitButtonText',
              'clientAIConfig.askAboutProductWidgetButtonColor',
              'clientAIConfig.askAboutProductWidgetButtonTextColor',
              'clientAIConfig.askAboutProductWidgetMaxWidth',
              'clientAIConfig.askAboutProductWidgetThumbsFeedbackEnabled',
              'clientAIConfig.askAboutProductWidgetProductQuestionsEnabled',
              'clientAIConfig.askAboutProductWidgetRefinementsEnabled',
              'clientAIConfig.askAboutProductWidgetProductCardsEnabled',
              'clientAIConfig.askAboutProductWidgetQuestionAlignment',
              'clientAIConfig.askAboutProductWidgetProductQuestionIcon',
              'clientAIConfig.askAboutProductWidgetProductQuestionIconSize',
              'clientAIConfig.askAboutProductWidgetContainerCss',
            ],
          },
          {
            name: 'categoryAnswers',
            label: 'Category Answers',
            type: 'subGroup',
            elements: [
              'clientAIConfig.categoryPageSuggestedPromptsEnabled',
              'clientAIConfig.categoryPageSuggestedPromptsMaxCount',
              'clientAIConfig.categoryPageSuggestedPromptsRefreshDays',
              'clientAIConfig.categoryPageSuggestedPromptsHeader',
              'clientAIConfig.categoryPageSuggestedPromptsStyle',
              'clientAIConfig.categoryPageSuggestedPromptsScrollStyle',
              'clientAIConfig.categoryPageSuggestedPromptsCustomCss',
              'clientAIConfig.categoryContentInstructions',
            ],
          },
          {
            name: 'Customer Context Tool',
            label: 'Customer Context Tool',
            type: 'subGroup',
            elements: [
              'clientAIConfig.customerContextToolEnabled',
              'clientAIConfig.productFeedID',
              'clientAIConfig.productFeedOrganizationID',
            ],
          },
          {
            name: 'selectionWizardTool',
            label: 'Selection Wizard Tool',
            type: 'subGroup',
            elements: [
              {
                type: 'component',
                name: 'selectionWizardTool',
              },
            ],
          },
        ],
      },
      {
        name: 'onPageContent',
        type: 'group',
        label: 'On-page Content',
        elements: [
          {
            name: 'shopifyWidgets',
            label: 'Shopify Widget Styles',
            type: 'subGroup',
            elements: [
              'clientAIConfig.shopifyWidgetsCustomCss',
            ],
          },
          {
            name: 'productStructuredData',
            label: 'Product Structured Data',
            type: 'subGroup',
            elements: [
              'clientAIConfig.productStructuredDataConfig',
            ],
          },
          {
            name: 'smartSummary',
            label: 'Smart Summary',
            type: 'subGroup',
            elements: [
              'clientAIConfig.productPageSmartSummaryEnabled',
              'clientAIConfig.categoryPageSmartSummaryEnabled',
              'clientAIConfig.productInsertMetadataEnabled',
              'clientAIConfig.categoryInsertMetadataEnabled',
              'clientAIConfig.pageSmartSummaryMaxBullets',
              'clientAIConfig.pageSmartSummaryGeneratedByAiLabel',
              'clientAIConfig.pageSmartSummaryGeneratedByAiLabelAlignment',
              'clientAIConfig.pageSmartSummaryAlignBulletsLeft',
              'clientAIConfig.pageSmartSummaryStyle',
              'clientAIConfig.pageSmartSummaryCustomCss',
              'clientAIConfig.pageGeoSummaryDisplayMode',
              'clientAIConfig.pageGeoSummaryMaxWidth',
              'clientAIConfig.guidesBlockTitle',
              'clientAIConfig.guidesGeoEnabled',
              'clientAIConfig.pageSmartSummarySystemPromptInstructions',
              'clientAIConfig.categoryPageSmartSummarySystemPromptInstructions',
            ],
          },
        ],
      },
      {
        name: 'suggestedPromptsAnswers',
        type: 'group',
        label: 'Suggested Prompts',
        elements: [
          {
            type: 'component',
            name: 'portalSuggestedPrompts',
          },
        ],
      },
      {
        name: 'shoppingAssistant',
        type: 'group',
        label: 'Shopping Assistant',
        elements: [
          {
            name: 'twoColumn',
            label: 'Two Column',
            type: 'twoColumn',
            elements: [
              {
                name: 'shoppingAssistantSettings',
                label: 'Shopping Assistant Settings',
                type: 'subGroup',
                elements: [
                  'clientAIConfig.shoppingAssistantInstructions',
                ],
              },
              {
                type: 'component',
                name: 'invokeShoppingAssistant',
              },
            ],
          },
        ],
      },
      {
        name: 'aiModeSearch',
        type: 'group',
        label: 'AI Mode Search',
        elements: [
          {
            name: 'twoColumn',
            label: 'Two Column',
            type: 'twoColumn',
            elements: [
              {
                name: 'aiModeSearchSettings',
                label: 'AI Mode Search Settings',
                type: 'subGroup',
                elements: [
                  'clientAIConfig.aiModeSearchInstructions',
                ],
              },
              {
                type: 'component',
                name: 'invokeAiModeSearch',
              },
            ],
          },
        ],
      },
      {
        name: 'dualSearch',
        type: 'group',
        label: 'Dual Search',
        elements: [
          {
            name: 'twoColumn',
            label: 'Two Column',
            type: 'twoColumn',
            elements: [
              {
                name: 'dualSearchSettings',
                label: 'Dual Search Settings',
                type: 'subGroup',
                elements: [
                  'clientAIConfig.dualSearchInstructions',
                ],
              },
              {
                type: 'component',
                name: 'invokeDualSearch',
              },
            ],
          },
        ],
      },
      {
        name: 'promptTesting',
        type: 'group',
        label: 'Prompt Testing',
        elements: [
          {
            type: 'component',
            name: 'promptTesting',
          },
        ],
      },
      {
        name: 'pgVectorSearch',
        type: 'group',
        label: 'Direct Search Testing',
        elements: [
          {
            type: 'component',
            name: 'pgVectorSearch',
          },
        ],
      },
    ],
  },
];

export { prepareDirtyPortalAIConfig } from '../components/clientAI/portalAIConfigPersistence';

const PortalAIConfig = () => {
  const store = useStore();
  const { currentPortal: portal } = store;
  const [shopPickerOpen, setShopPickerOpen] = useState(false);
  const [shopPickerError, setShopPickerError] = useState<string | null>(null);
  const [shopPickerOptions, setShopPickerOptions] = useState<any[]>([]);
  const [shopPickerValue, setShopPickerValue] = useState('');
  usePrompt({ isDirty: portal ? (portal.changeCount > 0) : false });

  const dirty = useRef(Schema.getPortalAIConfigEdits(portal as any));
  const [saveRevision, setSaveRevision] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);
  const source = Schema.getPortalAIConfigSource(portal as any);
  const formValues = useMemo(() => {
    const loaded = Schema.getPortalAIConfigSource(portal as any);
    let pending = loaded;
    if (loaded) {
      try {
        pending = prepareDirtyPortalAIConfig(portal, loaded, Schema.getPortalAIConfigEdits(portal as any));
      } catch {
        // Invalid pending edits are reported by Save; loading the form must remain safe.
      }
    }
    return loaded ? {
      ...pending.snapshot,
      clientAIConfig: { ...loaded.snapshot.clientAIConfig, ...(pending.config || {}) },
    } : undefined;
  }, [portal, portal?.syncState, saveRevision]);
  const previousPortal = useRef(portal);
  if (previousPortal.current !== portal) {
    dirty.current = Schema.getPortalAIConfigEdits(portal as any);
    previousPortal.current = portal;
  }
  const onUserFieldChange = useCallback((name: string, value: any) => {
    dirty.current.set(name, cloneDeep(Schema.normalizeFormValue(value)));
    portal?.addChange();
    return value === null; // Keep authored JSON null in the form even when its typed projection is nonnullable.
  }, [portal]);
  const onConfigFieldChange = useCallback((name: string, value: any) => {
    onUserFieldChange(`clientAIConfig.${name}`, value);
  }, [onUserFieldChange]);
  const buildClientAIConfigForSave = useCallback(() => {
    if (!portal || !source) throw Error('Reload this portal before saving its AI configuration.');
    return prepareDirtyPortalAIConfig(portal, source, dirty.current).config as Record<string, unknown>;
  }, [portal, source]);
  const savePortalWithConfig = useCallback(async (_config: Record<string, unknown>) => {
    if (!portal || !source) return undefined;
    const submitted = new Map(dirty.current);
    try {
      const prepared = prepareDirtyPortalAIConfig(portal, source, submitted);
      setSaveError(null);
      const result = await store.update('portal', portal, true, prepared);
      if (result?.success) {
        submitted.forEach((value, key) => {
          if (isEqual(dirty.current.get(key), value)) dirty.current.delete(key);
        });
        if (dirty.current.size === 0) setSaveRevision((revision) => revision + 1);
      }
      return result;
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Could not save AI configuration.');
      return undefined;
    }
  }, [store, portal, source]);

  const savePortal = useCallback(async () => {
    if (!portal) return Promise.reject(Error('No portal'));
    try {
    const config = buildClientAIConfigForSave();
    const portalOrganizationID = String(
      portal.organization?.id
        || portal.organizationID
        || store.currentOrganization?.id
        || '',
    );
    const activeStores = await store.listActiveShopifyConfigsForOrganization(portalOrganizationID);
    const pinnedShopifyConfigID = typeof config.productFeedID === 'string'
      ? config.productFeedID.trim()
      : '';
    const pinnedProductFeedOrganizationID = typeof config.productFeedOrganizationID === 'string'
      ? config.productFeedOrganizationID.trim()
      : '';
    const activeStoreIds = new Set(activeStores.map((shopifyConfig) => String(shopifyConfig.id)));
    const productFeedIsExplicitlyCrossOrg = Boolean(
      pinnedShopifyConfigID
      && pinnedProductFeedOrganizationID
      && pinnedProductFeedOrganizationID !== portalOrganizationID,
    );
    if (
      activeStores.length > 1
      && !productFeedIsExplicitlyCrossOrg
      && !activeStoreIds.has(pinnedShopifyConfigID)
    ) {
      setShopPickerOptions(activeStores);
      setShopPickerValue('');
      setShopPickerError(null);
      setShopPickerOpen(true);
      return undefined;
    }

    return await savePortalWithConfig(config);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Could not save AI configuration.');
      return undefined;
    }
  }, [buildClientAIConfigForSave, portal, savePortalWithConfig, store]);

  const handleCloseShopPicker = () => {
    setShopPickerOpen(false);
    setShopPickerError(null);
  };

  const handleConfirmShopPicker = async () => {
    if (!portal) return;
    const picked = shopPickerValue.trim();
    if (!picked) {
      setShopPickerError('Select the Shopify store for this portal.');
      return;
    }
    const config = buildClientAIConfigForSave();
    dirty.current.set('clientAIConfig.productFeedID', picked);
    config.productFeedID = picked;
    setShopPickerError(null);
    setShopPickerOpen(false);
    await savePortalWithConfig(config);
  };

  if (!portal) {
    return <div>Loading</div>;
  }

  if (portal.syncState !== 'loaded') {
    return <div>Loading..</div>;
  }

  return (
    <BasePromptModalProvider>
      <EditLayout
        title="AI Configuration"
        breadcrumbTitle={portal.publicName?.trim() || portal.name?.trim() || 'AI configuration'}
        buttonStack={(
          <Button
            color="primary"
            variant="contained"
            onClick={savePortal}
            disabled={portal.changeCount === 0}
          >
            Save
          </Button>
        )}
      >
        {saveError && <Typography role="alert" color="error">{saveError}</Typography>}
        <ItemEdit
          key={`${portal.id}:${saveRevision}`}
          formValues={formValues}
          onUserFieldChange={onUserFieldChange}
          modelName="portal"
          item={portal}
          formConfig={formConfig}
          collapsibleSections
          collapsibleSectionsTabKeys={['general', 'tools', 'onPageContent']}
          overrides={overrides}
          components={{
            invokeShoppingAssistant: <InvokeShoppingAssistantField portal={portal} />,
            invokeAiModeSearch: <InvokeSearchField portal={portal} variant="ai" />,
            invokeDualSearch: <InvokeSearchField portal={portal} variant="dual" />,
            promptTesting: <PromptTestingField portal={portal} configuration={formValues?.clientAIConfig} onConfigChange={onConfigFieldChange} />,
            portalSuggestedPrompts: <PortalSuggestedPromptsPanel portal={portal} />,
            selectionWizardTool: <SelectionWizardToolField portal={portal} configuration={formValues?.clientAIConfig} onConfigChange={onConfigFieldChange} />,
            pgVectorSearch: <PgVectorSearchPanel portal={portal} />,
          }}
          handleSubmit={savePortal}
          hideTitle
          autoSave
        />
        <Dialog
          open={shopPickerOpen}
          onClose={handleCloseShopPicker}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Select Shopify store</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                This organization has multiple active Shopify shops. Choose the shop for this portal before saving.
              </Typography>
              <TextField
                select
                label="Shopify store"
                value={shopPickerValue}
                onChange={(event) => setShopPickerValue(event.target.value)}
                error={Boolean(shopPickerError)}
                helperText={shopPickerError || ' '}
                fullWidth
              >
                {shopPickerOptions.map((shopifyConfig) => (
                  <MenuItem key={shopifyConfig.id} value={shopifyConfig.id}>
                    {shopifyConfig.name || shopifyConfig.shop || shopifyConfig.id}
                    {shopifyConfig.shop ? ` (${shopifyConfig.shop})` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseShopPicker}>Cancel</Button>
            <Button variant="contained" onClick={handleConfirmShopPicker}>Save</Button>
          </DialogActions>
        </Dialog>
      </EditLayout>
    </BasePromptModalProvider>
  );
};

export default observer(PortalAIConfig);
